/**
 * Shared ECharts building blocks, so every chart in the panel carries the same
 * marks and the same restraint.
 *
 * The mark specs are fixed: bars ≤ 22px with a 4px rounded cap on the end of the
 * stack, 2px lines with ≥ 8px markers ringed in the surface colour, area fills
 * as a gradient that reaches zero at the baseline, and hairline solid gridlines
 * one step off the surface. Text always wears text tokens — never a series
 * colour.
 *
 * Proportions OUTSIDE a plot are not drawn here: a share of a whole is a Meter,
 * plain CSS. A chart library is for data with an axis, and four ECharts
 * instances for four percentages was four canvases doing what four divs do
 * better.
 */

import { isRtl } from '../lib/i18n'

/* The UI family (tokens.css --font-sans). A canvas cannot read the custom property. */
export const FONT = '"Rawam Sans", "Droid Arabic Kufi", "Open Sans", "Segoe UI", Tahoma, system-ui, sans-serif'

/**
 * A canvas cannot read a custom property, so the palette is resolved from the
 * document at the moment an option is built, and cached until the theme moves.
 * The theme block in tokens.css is still the only place any of it is decided.
 *
 * All three modules that build chart options subscribe to the theme, so a
 * switch re-renders them, rebuilds their options against this palette and
 * redraws — which is why nothing here has to be reactive itself.
 */
const FALLBACK = {
  surface: '#ffffff',
  grid: '#eef2f6',
  axis: '#98a2b3',
  ink: '#3b465c',
  success: '#0f8a6d',
  failure: '#b42318',
  incomplete: '#d97706',
  absent: '#64748b',
  info: '#0284c7',
  gold: '#c9a24b',
}

let cache = null
let cachedTheme = null

const hex2 = (value) => Math.round(Math.min(255, Math.max(0, value))).toString(16).padStart(2, '0')

/**
 * A token's USED colour as `#rrggbb`, for the text tokens that are written as `color-mix()`.
 *
 * The computed value of a custom property is its text, so `--accent-ink` reads back as
 * "color-mix(…)" — which a canvas may draw but `alpha()` below cannot take apart. A probe
 * element resolves it the way the page does; the browser hands back `rgb(…)` or, for a mix,
 * `color(srgb …)`, and both are turned into hex here.
 */
function resolveColor(name, fallback) {
  const probe = document.createElement('span')
  probe.style.cssText = `position:absolute;visibility:hidden;color:var(${name}, ${fallback})`
  document.documentElement.appendChild(probe)
  const used = getComputedStyle(probe).color
  probe.remove()
  const rgb = used.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/)
  if (rgb) return `#${hex2(rgb[1])}${hex2(rgb[2])}${hex2(rgb[3])}`
  const srgb = used.match(/^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)/)
  if (srgb) return `#${hex2(srgb[1] * 255)}${hex2(srgb[2] * 255)}${hex2(srgb[3] * 255)}`
  return fallback
}

/** The live palette. The light values stand in outside a browser. */
export function palette() {
  if (typeof document === 'undefined') return FALLBACK
  const root = document.documentElement
  const theme = root.dataset.theme ?? 'light'
  if (cache && cachedTheme === theme) return cache
  const style = getComputedStyle(root)
  const read = (name, fallback) => style.getPropertyValue(name).trim() || fallback
  cachedTheme = theme
  cache = {
    /* Text inks, resolved: values and the organisation line wear these, never a series hue. */
    strong: resolveColor('--text-strong', '#1c2740'),
    muted: resolveColor('--text-muted', '#64748b'),
    accentInk: resolveColor('--accent-ink', '#0b5f4d'),
    /* The one flag that means "notably behind the organisation" — the ink the table's flag
       wears, so the dot in a chart and the word in a cell are the same colour. */
    behind: resolveColor('--warning-ink', '#985606'),
    surface: read('--bg-surface', FALLBACK.surface),
    grid: read('--chart-grid', FALLBACK.grid),
    axis: read('--chart-axis', FALLBACK.axis),
    ink: read('--text', FALLBACK.ink),
    success: read('--chart-success', FALLBACK.success),
    failure: read('--chart-failure', FALLBACK.failure),
    incomplete: read('--chart-incomplete', FALLBACK.incomplete),
    absent: read('--chart-absent', FALLBACK.absent),
    info: read('--chart-info', FALLBACK.info),
    /* THE BRAND'S SECOND COLOUR. `--chart-gold` has been declared in tokens.css since the
       palette was written and read by nothing, so every chart drew in status hues — and a
       chart that is not about pass and fail had to borrow one. */
    gold: read('--chart-gold', FALLBACK.gold),
    /* Ink for a label drawn INSIDE a filled band. The fills lift toward white
       in dark, so white-on-fill stops working there and this goes dark. */
    onFill: read('--on-status', '#ffffff'),
  }
  return cache
}

/* Read the way the four constants these replaced did. Getters, so each access
   resolves against whatever theme is on screen when the option is built. */
const P = {
  get surface() {
    return palette().surface
  },
  get grid() {
    return palette().grid
  },
  get axis() {
    return palette().axis
  },
  get ink() {
    return palette().ink
  },
}

/**
 * The four programme outcomes. They mean pass/fail, so they take status colours
 * rather than categorical hues; the set is validated in tokens.css.
 *
 * `absent` is "nothing happened yet", not a fifth outcome competing with the
 * other three — it used to be a saturated indigo, and since it is also the
 * tallest bar in every programme it read as the chart's headline. A slate keeps
 * it legible and correctly quiet.
 */
export const OUTCOME_COLORS = {
  get success() {
    return palette().success
  },
  get failure() {
    return palette().failure
  },
  get incomplete() {
    return palette().incomplete
  },
  get absent() {
    return palette().absent
  },
}

/** Fixed order, since the object above has no own enumerable data keys. */
export const OUTCOME_KEYS = ['success', 'failure', 'incomplete', 'absent']

/* The three objects below are module-level, so a plain `color: P.axis` would
   resolve once at import and freeze the light palette into every chart for the
   life of the tab. As getters they resolve when ECharts reads them, which is
   after a theme switch has already re-pointed the variables. */
export const baseText = {
  get color() {
    return P.axis
  },
  fontFamily: FONT,
  fontSize: 12,
}

/**
 * The names a horizontal chart is read by — a department, a policy, a topic. They are the
 * row's label, not its scale, so they take the body ink at a size a name can be read at, where
 * the scale's ticks keep the quieter axis ink.
 */
export const nameText = {
  get color() {
    return P.ink
  },
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: 500,
}

/**
 * A halo for a figure printed over a plot: a ring of the surface colour around each glyph, so a
 * value stays legible where it crosses a gridline, a dashed reference or another mark.
 */
export const halo = {
  get textBorderColor() {
    return P.surface
  },
  textBorderWidth: 3,
}

/** `#rrggbb` at an alpha, for fills that have to fade into the surface. */
export const alpha = (hex, a) => {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

/**
 * The wash under a line: the series colour at 7%, flat.
 *
 * It used to be a gradient fading to the baseline — the lit "glow under the
 * curve" every chart template ships with (general-style pass, 2026-10). At 7%
 * two overlapping washes stay readable, and the line carries the reading.
 */
const areaFill = (color) => alpha(color, 0.07)

export const tooltip = (trigger = 'axis') => ({
  trigger,
  axisPointer: { type: trigger === 'axis' ? 'line' : 'none', lineStyle: { color: P.grid } },
  backgroundColor: P.surface,
  borderColor: P.grid,
  borderWidth: 1,
  padding: [12, 14],
  /* The tooltip is a real DOM node ECharts styles inline, so this one cannot be
     a token — the shadow is written for whichever theme is on screen. */
  extraCssText: `box-shadow: 0 4px 12px -2px rgba(${
    palette().surface === '#ffffff' ? '16,24,40,.1' : '0,0,0,.5'
  }); border-radius: 8px;`,
  textStyle: { color: P.ink, fontFamily: FONT, fontSize: 13 },
})

export const legend = {
  bottom: 0,
  icon: 'circle',
  itemWidth: 9,
  itemHeight: 9,
  itemGap: 22,
  textStyle: {
    get color() {
      return P.ink
    },
    fontFamily: FONT,
    fontSize: 12.5,
  },
}

/* `containLabel` is deprecated in ECharts 6 and logs on every render; the docs
   give this pair as its exact equivalent. */
export const grid = {
  top: 24,
  left: 8,
  right: 8,
  bottom: 44,
  outerBoundsMode: 'same',
  outerBoundsContain: 'axisLabel',
}

/**
 * Wraps a long category name onto a second line at a word boundary instead of
 * cutting it at `max`. Programme names here run to 38 characters — truncated to
 * one line, four of the seven read as the same "…برنامج التوعية بالأمن", which
 * is the one thing an axis label has to avoid. Only past two lines does it
 * ellipsise.
 */
const wrapLabel = (value, max) => {
  if (value.length <= max) return value
  const words = value.split(' ')
  const lines = ['']
  for (const word of words) {
    const line = lines.length - 1
    if (!lines[line]) lines[line] = word
    else if (`${lines[line]} ${word}`.length <= max) lines[line] += ` ${word}`
    else if (lines.length < 2) lines.push(word)
    else {
      lines[line] = `${lines[line].slice(0, max - 1)}…`
      break
    }
  }
  return lines.join('\n')
}

/*
 * Canvas text takes the chart host's dir="ltr", so an Arabic name that begins or ends with
 * Latin or punctuation was laid out backwards — "QA برنامج فيديو تجريبي", a hyphen at the
 * front of the line. FSI…PDI gives each line the direction of its own first letter, as the
 * funnel's percentages do; a line with no letters (a date) stays left-to-right.
 */
const isolateLines = (text) => text.split('\n').map((line) => `⁨${line}⁩`).join('\n')

/**
 * `maxLabel` wraps long category names rather than letting them collide or be
 * clipped; the tooltip still carries the full name.
 */
export const categoryAxis = (data, { maxLabel } = {}) => ({
  type: 'category',
  data,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    ...baseText,
    hideOverlap: true,
    ...(maxLabel ? { lineHeight: 15 } : null),
    formatter: (value) => isolateLines(maxLabel ? wrapLabel(String(value), maxLabel) : String(value)),
  },
})

export const valueAxis = {
  type: 'value',
  axisLine: { show: false },
  axisTick: { show: false },
  /* Ticks that would touch are dropped rather than printed over each other — on a phone a
     0–100% scale has room for three, not six. */
  axisLabel: { ...baseText, hideOverlap: true, formatter: (value) => value.toLocaleString('en-US') },
  /* A SOLID HAIRLINE, one step off the surface. Dashes are a texture: thirty of them across a
     wide plot shimmer and compete with the data, where a hairline recedes until the eye goes
     looking for it. A dash is kept for the one line that is a reference rather than a scale —
     the organisation's rate drawn over a ranking. */
  splitLine: {
    lineStyle: {
      get color() {
        return P.grid
      },
      width: 1,
      type: 'solid',
    },
  },
}

/* ═══════════════════════════════════════════════════════════════════════════
   Big data — a window over a long axis instead of an ever taller chart.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Rows a horizontal ranking shows at once before it scrolls inside its own window. */
export const ROW_WINDOW = 14

/**
 * A phone-width screen. Read when an option is built: the name column of a horizontal chart is
 * a fixed width, and 170px of names beside a 150px plot left the bars nowhere to be read.
 */
export const compactScreen = () => typeof window !== 'undefined' && window.innerWidth < 640

/** The name column of a horizontal chart: its width and the characters a line wraps at. */
export const nameColumn = (width = 168) =>
  compactScreen() ? { labelWidth: 104, labelChars: 13 } : { labelWidth: width, labelChars: Math.round(width / 7.6) }

/** Points a time series shows at once before older ones move behind a slider. */
export const TIME_WINDOW = 24

/* Touch screens get the slider only: a drag inside the plot would take the page's own vertical
   scroll away from a finger that only meant to move past the chart. */
const coarsePointer = () =>
  typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches)

/* A slider dressed as a scrollbar: a track in the gridline colour and a thumb one step darker.
   No handles, no labels, no data shadow — it moves a window, it does not choose one. */
const scrollbarSlider = () => ({
  type: 'slider',
  zoomLock: true,
  brushSelect: false,
  showDetail: false,
  showDataShadow: false,
  handleSize: 0,
  moveHandleSize: 0,
  borderColor: 'transparent',
  borderRadius: 999,
  backgroundColor: P.grid,
  fillerColor: alpha(palette().axis, 0.45),
  handleStyle: { opacity: 0 },
  emphasis: { handleStyle: { opacity: 0 } },
})

/**
 * The window over a horizontal ranking with more than `rows` rows: the first `rows` show, and a
 * thin scrollbar on the far side from the names moves through the rest.
 *
 * The mouse wheel is deliberately LEFT TO THE PAGE. ECharts swallows every wheel event over a
 * zoomable plot, so a wheel-driven window would trap the reader halfway down a long report;
 * dragging the plot or the bar moves it instead. Returns `undefined` when everything fits.
 */
export function rowZoom(count, { rows = ROW_WINDOW, top = 24, bottom = 32 } = {}) {
  if (count <= rows) return undefined
  const window = { startValue: 0, endValue: rows - 1 }
  return [
    {
      ...scrollbarSlider(),
      ...window,
      yAxisIndex: 0,
      orient: 'vertical',
      width: 8,
      top,
      bottom,
      /* The y axis is inverted so row one is at the top; the bar has to run the same way. */
      inverse: true,
      ...(isRtl ? { left: 2 } : { right: 2 }),
    },
    {
      type: 'inside',
      ...window,
      yAxisIndex: 0,
      zoomLock: true,
      zoomOnMouseWheel: false,
      moveOnMouseWheel: false,
      moveOnMouseMove: !coarsePointer(),
    },
  ]
}

/** The height a ranking of `count` rows takes, windowed past `rows`. */
export const rowsHeight = (count, { rows = ROW_WINDOW, row = 30, chrome = 72 } = {}) =>
  Math.max(180, Math.min(count, rows) * row + chrome)

/**
 * The window over a long time series: the most recent `points` show, and a slider under the
 * axis reaches back. Ctrl + wheel zooms (the convention maps taught everyone); a plain wheel
 * scrolls the page.
 *
 * @param {'end'|'start'} [from]  which end the window opens on: the latest points of a series
 *   (`end`), or the first rows of a ranking read along the x axis (`start`)
 * @returns {{ dataZoom?: object[], bottom: number }}  `bottom` is the grid's bottom margin with
 *   room left for the slider between the axis labels and the legend.
 */
export function timeZoom(count, { points = TIME_WINDOW, legend: withLegend = true, base: baseBottom, from = 'end' } = {}) {
  const base = baseBottom ?? (withLegend ? 44 : 12)
  if (count <= points) return { dataZoom: undefined, bottom: base }
  const window = from === 'start' ? { startValue: 0, endValue: points - 1 } : { startValue: count - points, endValue: count - 1 }
  return {
    bottom: base + 30,
    dataZoom: [
      {
        ...scrollbarSlider(),
        ...window,
        xAxisIndex: 0,
        zoomLock: false,
        height: 8,
        bottom: base + 6,
      },
      {
        type: 'inside',
        ...window,
        xAxisIndex: 0,
        zoomOnMouseWheel: 'ctrl',
        moveOnMouseWheel: false,
        moveOnMouseMove: !coarsePointer(),
      },
    ],
  }
}

/**
 * Column series: capped width, square at the baseline, 4px rounded cap on the
 * topmost segment only.
 *
 * `stack` groups segments into one column — the right call when one series dwarfs
 * the others, because side-by-side would flatten the small ones to nothing. The 2px
 * separation between segments is a surface-coloured border, never a stroke in the
 * series colour.
 */
export const barSeries = (
  name,
  data,
  color,
  { stack, cap = false, horizontal = false, label = false } = {},
) => ({
  name,
  type: 'bar',
  data,
  stack,
  barMaxWidth: 24,
  barCategoryGap: '34%',
  barGap: '18%',
  itemStyle: {
    color,
    /* The cap belongs on the END of the stack, which is the top of a column and
       the far side of a horizontal bar. Ungrouped columns round both top
       corners; a stack rounds only its last segment. */
    borderRadius: stack ? (cap ? (horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]) : 0) : [4, 4, 0, 0],
    ...(stack ? { borderColor: P.surface, borderWidth: 2 } : null),
  },
  ...(label
    ? {
        label: {
          show: true,
          position: 'top',
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          color: P.ink,
          /* Zero is the commonest value in this data and the least worth
             printing — seven programmes with three empty outcomes each would put
             twenty-one noughts on the plot. */
          formatter: ({ value }) => (value ? value.toLocaleString('en-US') : ''),
        },
      }
    : null),
  emphasis: { focus: 'series' },
})

/** Line series: 2px stroke, ringed marker, and a wash that fades to nothing. */
export const lineSeries = (name, data, color, { area = true } = {}) => ({
  name,
  type: 'line',
  data,
  /* Thousands of daily points are drawn as the shape they make, not as thousands of segments:
     largest-triangle sampling keeps every peak and trough the eye would see. */
  sampling: 'lttb',
  smooth: 0.25,
  /* A smoothed count must never dip below the readings either side of it — a curve that
     undershoots zero between two quiet days draws a negative number of exams. */
  smoothMonotone: 'x',
  symbol: 'circle',
  symbolSize: 8,
  showSymbol: data.length <= 24,
  lineStyle: { width: 2, cap: 'round', join: 'round', color },
  itemStyle: { color, borderColor: P.surface, borderWidth: 2 },
  /* Hovering one series dims the other, which is the only way to follow a
     single plot through a couple of hundred daily points. */
  emphasis: { focus: 'series' },
  blur: { lineStyle: { opacity: 0.18 }, areaStyle: { opacity: 0.18 } },
  ...(area ? { areaStyle: { color: areaFill(color) } } : null),
})

/**
 * Two lines, and the row height that has to go with them.
 *
 * A wrapped axis label grows downward without ECharts reserving any space for
 * it, and it is centred on its own tick — so a three-line name spills half a
 * line into the row above and half into the row below. On the paths ranking that
 * meant two programme names printed on top of each other and one row losing its
 * label entirely. Capped at two lines, every row is the same height and the axis
 * can be given a matching one.
 */
const LABEL_LINE = 16
const LABEL_LINES = 2
export const AXIS_LABEL_HEIGHT = LABEL_LINE * LABEL_LINES

/** The row height a horizontal plot needs: two lines of label, plus air. */
export const rowHeight = (rows) => Math.max(220, rows * (AXIS_LABEL_HEIGHT + 14) + 56)

export const horizontalAxes = (names, { labelChars = 16, labelWidth = 150, max } = {}) => ({
  /* Zero at the READING START. A horizontal bar is a sentence — it begins where
     the reader begins — and left-to-right growth in an Arabic layout runs
     backwards under labels that are themselves right-ranged. In English the
     reader begins on the left, so nothing is inverted. */
  xAxis: { ...valueAxis, inverse: isRtl, ...(max == null ? null : { max }) },
  yAxis: {
    type: 'category',
    data: names,
    /* ECharts runs a category axis bottom-up; inverting puts the first row at
       the top, which is where an ordered list is read from. */
    inverse: true,
    /* The names belong at the reading start too — beside the foot of each bar
       rather than across the plot from it. */
    position: isRtl ? 'right' : 'left',
    axisLine: { show: false },
    axisTick: { show: false },
    /*
     * The name column: WRAPPED BY THE FORMATTER, RESERVED BY THE WIDTH.
     *
     * Those are two different jobs and the bug was doing only the first. The
     * formatter breaks the string at a word boundary and ellipsises past its
     * second line, which is right — but with no width declared, and the axis at
     * `position: 'right'`, nothing reserved room for the result: the label was
     * drawn past the canvas edge and CLIPPED, so `إدارة المشاريع والتحول` came
     * out as `رة المشاريع والتحول`, missing its first word, silently, and only
     * for the longest names.
     *
     * `width` reserves the column and `grid.right` steps the plot back by the
     * same amount — they have to be the same number, which is why the caller
     * passes one constant to both.
     *
     * `overflow: 'truncate'` rather than `'break'`: breaking by width wraps the
     * ALREADY-wrapped string a second time, and two wrappers put these names on
     * three lines, which overlapped the row below because `rowHeight()` budgets
     * two. One wrapper, one reservation.
     */
    axisLabel: {
      ...nameText,
      lineHeight: LABEL_LINE,
      width: labelWidth,
      overflow: 'truncate',
      formatter: (value) => isolateLines(wrapLabel(String(value), labelChars)),
    },
  },
})

/**
 * The four programme outcomes as one ring.
 *
 * A ring rather than the flat 100% bar this replaced: the bar was honest but it
 * was also the third horizontal bar on a card that already carries a stacked bar
 * plot, and three bar treatments in one card is one visual idea doing three
 * jobs. A ring is the same reading in a form the page does not otherwise use,
 * and it leaves its own middle free for the number the card is really about.
 *
 * No labels on the slices — three of the four are slivers here, and ECharts
 * would run leader lines out to them. The key beside the ring names them.
 */
/** Whether there is anything to share out: four zeros are no data, not four quarters. */
export const hasShares = (shares) => OUTCOME_KEYS.some((key) => Number(shares?.[key]) > 0)

const DONUT_RING = { radius: ['64%', '92%'], center: ['50%', '50%'] }

export const donutOption = (shares, labels) => {
  /* NOTHING TO DIVIDE IS AN EMPTY RING. ECharts draws a pie whose values sum to zero as
     equal slices (`stillShowZeroSum`), so a period with no results showed pass, fail,
     incomplete and absent as a quarter each beside four "0%" labels. With no data it is
     one bare track, in the colour every other empty track on the page uses. */
  if (!hasShares(shares)) {
    return {
      tooltip: { show: false },
      series: [
        {
          type: 'pie',
          ...DONUT_RING,
          silent: true,
          label: { show: false },
          labelLine: { show: false },
          data: [{ value: 1, itemStyle: { color: P.grid } }],
        },
      ],
    }
  }

  return donutWithShares(shares, labels)
}

const donutWithShares = (shares, labels) => ({
  tooltip: {
    ...tooltip('item'),
    formatter: (params) => `${params.name}<br/><b>${params.value}%</b>`,
  },
  series: [
    {
      type: 'pie',
      ...DONUT_RING,
      /* A share at 0 is not drawn at all, rather than as a sliver of border. */
      stillShowZeroSum: false,
      /* The data arrives in outcome order — pass, fail, incomplete, absent —
         which is a meaning, not a magnitude. Re-sorting would put the biggest
         slice first and quietly change what the chart is about. */
      sort: 'none',
      avoidLabelOverlap: false,
      label: { show: false },
      labelLine: { show: false },
      itemStyle: {
        borderColor: P.surface,
        borderWidth: 2,
        borderRadius: 3,
      },
      emphasis: { scaleSize: 4, itemStyle: { borderWidth: 2 } },
      data: OUTCOME_KEYS.map((key) => ({
        name: labels[key],
        value: shares[key],
        itemStyle: { color: OUTCOME_COLORS[key] },
      })),
    },
  ],
})

/* ═══════════════════════════════════════════════════════════════════════════
   Platform-KPI shapes — ported from the rawam-ui reference panel
   (features/platform-kpis). Three forms, each used more than once.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The participation funnel: how many of the targeted population reach each
 * stage. Every stage is a subset of the one before it, so the shape only ever
 * narrows — which is the whole reason it is drawn as a funnel and not as bars.
 */
export const funnelOption = (stages) => {
  /* The share each stage keeps of the population the funnel STARTED with. It is
     the whole question a funnel is asked — "how many of the targeted actually
     passed" — and it was the one thing the bands did not say. */
  const top = stages[0]?.value || 0
  const share = (value) => (top ? Math.round((value / top) * 100) : null)

  return {
    /* No tooltip. Every band already prints its own name and figure inside
       itself, so the popup only repeated them — while covering the band below,
       hiding the one thing it did not already say. */
    tooltip: { show: false },
    series: [
      {
        type: 'funnel',
        top: 8,
        bottom: 8,
        left: '6%',
        right: '6%',
        minSize: '34%',
        maxSize: '100%',
        sort: 'none',
        gap: 4,
        label: {
          position: 'inside',
          fontFamily: FONT,
          fontSize: 12,
          color: '#ffffff',
          formatter: (params) => {
            const pct = share(params.value)
            const head = `${params.name} · ${params.value.toLocaleString('en-US')}`
            // The first band IS the whole, so "100%" on it says nothing.
            //
            // FSI…PDI around the percentage: canvas text is laid out by the
            // browser's bidi algorithm, and `%` is a weak terminator, so in an
            // Arabic run it detached from its own digits and rendered as
            // "%بدأوا المحتوى · 39 · 30". Isolated, it stays with its number.
            return params.dataIndex === 0 || pct == null
              ? head
              : `${head} · ⁨${pct}%⁩`
          },
        },
        labelLine: { show: false },
        itemStyle: { borderWidth: 0, borderRadius: 4 },
        emphasis: { disabled: true },
        data: stages.map((stage, index) => ({
          name: stage.label,
          value: stage.value,
          /* One hue, stepped: the stages are one population narrowing, not four
             different subjects, so they share the accent and separate by depth. */
          itemStyle: {
            color: `color-mix(in srgb, ${OUTCOME_COLORS.success} ${100 - index * 16}%, #ffffff)`,
          },
        })),
      },
    ],
  }
}

/**
 * A ranking drawn as 100%-stacked horizontal bars: the achieved portion against
 * that row's own remaining population.
 *
 * A plain ranked bar of the RATE alone paints a two-person department at 100%
 * exactly like a four-hundred-person one, so the tooltip always carries the real
 * head-counts. `average` draws the organisation-wide rate as a benchmark, which
 * is what turns a ranking into a judgement: below the line is behind the
 * organisation, not merely lower than the row above.
 *
 * Rows arrive already ordered — this never re-sorts, so what a panel's title
 * promises is what it plots.
 */
/* The department/programme name column. Declared once: the axis reserves it
   and the grid steps back by it, and the two have to be the same number. */
const RANK_LABEL_WIDTH = 150

/* The plot's side margins for a horizontal chart: the name column on the
   reading-start side, a hair on the other. */
export const labelGutter = (width, far = 8) =>
  isRtl ? { left: far, right: width + 12 } : { left: width + 12, right: far }

/* The unfilled part of a row. Recessive on purpose: the accent segment is what
   the eye should follow. It is the gridline colour — one step off the surface
   in either theme — read live rather than fixed, because as a light hex it
   painted every dark-mode track bright white and swallowed the ink label
   printed over it. */
const RANK_TRACK = {
  get color() {
    return P.grid
  },
}

export const rankedBarsOption = (rows, { valueLabel, remainderLabel, average } = {}) => {
  /* No pre-truncation: the axis wraps now, so a name that used to be cut at 40
     characters — which was still enough to render two different programmes
     identically — is simply shown. */
  const labels = rows.map((row) => row.name)
  return {
    tooltip: {
      ...tooltip('axis'),
      formatter: (params) => {
        const row = rows[params[0].dataIndex]
        return [
          `<b>${row.name}</b>`,
          `${valueLabel}: ${row.value.toLocaleString('en-US')} / ${row.total.toLocaleString('en-US')}`,
          `${row.rate}%`,
        ].join('<br/>')
      },
    },
    /* The track's swatch is named here rather than taken from its series,
       which paints nothing now. */
    legend: {
      ...legend,
      bottom: 0,
      data: [valueLabel, { name: remainderLabel, itemStyle: { color: RANK_TRACK.color } }],
    },
    /* `top` is headroom for the benchmark label, which rides the TOP of its
       dashed line; at 8px it was drawn against the plot's ceiling and clipped. */
    grid: {
      top: 24,
      ...labelGutter(RANK_LABEL_WIDTH),
      bottom: 42,
    },
    ...(() => {
      const axes = horizontalAxes(labels, { max: 100, labelWidth: RANK_LABEL_WIDTH })
      return {
        xAxis: { ...axes.xAxis, axisLabel: { ...baseText, formatter: (value) => `${value}%` } },
        yAxis: axes.yAxis,
      }
    })(),
    series: [
      {
        name: valueLabel,
        type: 'bar',
        stack: 'rate',
        barMaxWidth: 18,
        /* Rounded on BOTH ends. A 100% row and a 4% row were the same square
           slab at different lengths; a capsule reads as a filled amount. */
        /* THE BRAND'S SECOND COLOUR, not the success green. This chart ranks departments by
           how much they took part; it says nothing about anyone passing. Drawn in the pass
           colour it made a claim the figures do not support, and it put a status hue on the
           one panel where every other mark IS a status. Gold is the token's own stated job —
           "indicators, never status". */
        itemStyle: { color: palette().gold, borderRadius: 9 },
        showBackground: true,
        backgroundStyle: { color: RANK_TRACK.color, borderRadius: 9 },
        /* Every row prints its own rate. A ranking whose figures are only
           available on hover makes the reader interrogate twelve rows one at a
           time to learn what it already knows. */
        /* The ink flips with the bar. A short capsule cannot contain its own
           label, so the figure ends up over the pale remainder track — and at
           0% the row printed white text on near-white and vanished. Rich-text
           styles are how a per-datum colour is expressed here; `labelLayout`
           can move a label but not recolour it. */
        label: {
          show: true,
          /* Anchored at the bar's FOOT — beside the axis, growing into the
             plot — so a 0% row's figure never runs back over its own name. */
          position: isRtl ? 'insideRight' : 'insideLeft',
          distance: 8,
          fontFamily: FONT,
          fontSize: 11,
          fontWeight: 600,
          formatter: ({ dataIndex }) => {
            const { rate } = rows[dataIndex]
            return rate < 6 ? `{out|${rate}%}` : `{in|${rate}%}`
          },
          rich: {
            in: { color: palette().onFill, fontFamily: FONT, fontSize: 11, fontWeight: 600 },
            out: { color: P.ink, fontFamily: FONT, fontSize: 11, fontWeight: 600 },
          },
        },
        data: rows.map((row) => row.rate),
        ...(average == null
          ? null
          : {
              markLine: {
                symbol: 'none',
                silent: true,
                label: {
                  /* At the START of the line — the TOP of the plot — where there
                     is nothing else. At `end` it sat on the axis, and with the
                     organisation average at 7% it printed "7%" a few pixels from
                     the "0%" tick, so the chart appeared to label its own
                     baseline twice. */
                  position: 'start',
                  distance: 6,
                  formatter: `${average}%`,
                  fontFamily: FONT,
                  fontSize: 11,
                  fontWeight: 'bold',
                  color: P.ink,
                  backgroundColor: P.surface,
                  padding: [2, 4],
                  borderRadius: 4,
                },
                lineStyle: { color: P.ink, type: 'dashed', width: 1, opacity: 0.55 },
                data: [{ xAxis: average }],
              },
            }),
      },
      {
        name: remainderLabel,
        type: 'bar',
        stack: 'rate',
        barMaxWidth: 18,
        /* DRAWN AS THE VALUE SERIES' BACKGROUND, not as a second segment.

           Stacked, the two segments were both fully rounded, so every row met
           its neighbour rounded-end to rounded-end and pinched — two capsules
           laid end to end rather than one track with an amount filled in. And
           the join could not be squared off per series either, because the end
           that should stay round is the OUTER one, which swaps sides at 0% and
           at 100%.

           `showBackground` on the value series draws one full-width rounded
           track per category, under the fill, at every value — no join to
           show. This series survives only so the legend keeps its second
           entry; it paints nothing, and the legend takes its swatch colour
           from `legend.data` below instead of from here. */
        itemStyle: { color: 'transparent' },
        silent: true,
        data: rows.map((row) => Math.max(0, 100 - row.rate)),
      },
    ],
  }
}

/**
 * The stages of one campaign as columns: how far, out of everyone reached, the
 * recipients got. `sent` is the first column at 100% — the whole every other
 * column is a part of — and the rest fall away from it in the order the strip
 * of KPI cards above the chart lists them, so each column stands under its own
 * card.
 *
 * NOT A FUNNEL. The stages overlap rather than nest (someone who reported never
 * counts as clicked), so a narrowing shape would claim an order the data does
 * not have; columns on one axis make no such claim. And not a meter: a track
 * with a fill reads as progress toward something, where these are five
 * measurements side by side — and a zero here is a column of no height under
 * a printed "0%", which nobody mistakes for a sliver.
 *
 * Each column wears the status colour its stage carries everywhere else in the
 * product — the card above it, the badge in the table below — so the colour is
 * a meaning the reader already holds, not a series key to learn; `sent` is the
 * one stage that is no verdict and wears the quiet slate.
 *
 * @param {{name: string, count: number, rate: number, tone: string}[]} stages
 *   in journey order; `tone` is a palette key
 * @param {{total: number, ofLabel: (count: number, total: number) => string}} opts
 */
export const stageColumnsOption = (stages, { total, ofLabel }) => {
  const colours = palette()
  return {
    tooltip: {
      ...tooltip('item'),
      /* The chart host is dir="ltr", so "5 من 12" was laid out backwards inside
         it. Each line takes its direction from its own first strong character. */
      formatter: ({ dataIndex }) => {
        const stage = stages[dataIndex]
        return [`<b>${stage.name}</b>`, ofLabel(stage.count, total), `${stage.rate}%`]
          .map((line) => `<div dir="auto">${line}</div>`)
          .join('')
      },
    },
    grid: { ...grid, top: 32, bottom: 8 },
    xAxis: (() => {
      const axis = categoryAxis(
        stages.map((stage) => stage.name),
        { maxLabel: 9 },
      )
      return {
        ...axis,
        /* The journey runs in the reading direction — the same order as the
           cards above, so the column for a stage sits under its card. */
        inverse: isRtl,
        /* Five names, ALL of them: a phone-width plot used to drop the fourth
           rather than let it touch its neighbours, and a column with no name
           under it is a column nobody can read. Wrapped at nine characters
           they fit five abreast at 400px. */
        axisLabel: { ...axis.axisLabel, interval: 0, hideOverlap: false },
      }
    })(),
    yAxis: {
      ...valueAxis,
      max: 100,
      interval: 25,
      /* The scale stands at the origin — beside the first column, on the
         reading-start side — rather than across the plot from it. */
      position: isRtl ? 'right' : 'left',
      axisLabel: { ...baseText, formatter: (value) => `${value}%` },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 44,
        barCategoryGap: '45%',
        itemStyle: { borderRadius: [6, 6, 0, 0] },
        /* Every column prints its share, zero included — the zero is the one
           figure this chart exists to make unmistakable. The count rides in the
           tooltip; the cards above already print it. */
        label: {
          show: true,
          position: 'top',
          distance: 6,
          fontFamily: FONT,
          fontSize: 12,
          fontWeight: 600,
          color: P.ink,
          formatter: ({ value }) => `${value}%`,
        },
        data: stages.map((stage) => ({
          value: stage.rate,
          itemStyle: { color: colours[stage.tone] ?? colours.absent },
        })),
        emphasis: { disabled: true },
      },
    ],
  }
}
